import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Upload, X, FileText, Image as ImageIcon, Search, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Smart file picker dialog with two tabs:
 *   1) Upload New     - opens native file picker
 *   2) Pick from Library - lists items from Media or Document Library
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   user: { token, username, role }
 *   kind: "image" | "document" | "any"
 *   onPick: ({ source: "upload"|"library", file?: File, libraryItem?: object }) => void
 *   title: optional string
 */
const FilePickerDialog = ({ open, onClose, user, kind = 'any', onPick, title }) => {
  const [tab, setTab] = useState('upload'); // 'upload' | 'library'
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const isImageKind = kind === 'image';
  const librarySource = isImageKind ? 'media' : 'documents';
  const acceptAttr = isImageKind
    ? 'image/*'
    : kind === 'document'
      ? '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv'
      : '*/*';

  useEffect(() => {
    if (!open) return;
    if (tab !== 'library') return;
    setLoading(true);
    const url = isImageKind ? `${API}/media/files` : `${API}/documents/files`;
    axios.get(url, { headers: { Authorization: `Bearer ${user.token}` } })
      .then(r => setItems(r.data?.files || r.data || []))
      .catch(() => toast.error('Failed to load library'))
      .finally(() => setLoading(false));
  }, [open, tab, isImageKind, user.token]);

  const filtered = items.filter(it => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (it.original_name || '').toLowerCase().includes(q) ||
           (it.description || '').toLowerCase().includes(q) ||
           (it.category || '').toLowerCase().includes(q);
  });

  const handleUploadClick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    onPick({ source: 'upload', file: f });
    onClose();
  };

  const handlePickLibrary = (item) => {
    onPick({ source: 'library', libraryItem: item, libraryKind: librarySource });
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-[65] flex items-center justify-center p-3 sm:p-6" data-testid="file-picker-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">{title || 'Add File'}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="file-picker-close">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 bg-slate-50">
          <button
            onClick={() => setTab('upload')}
            data-testid="picker-tab-upload"
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${tab === 'upload' ? 'bg-white text-indigo-700 border-b-2 border-indigo-600' : 'text-slate-600 hover:bg-slate-100'}`}
          >
            <Upload className="w-4 h-4 inline mr-1.5" />
            Upload New
          </button>
          <button
            onClick={() => setTab('library')}
            data-testid="picker-tab-library"
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${tab === 'library' ? 'bg-white text-indigo-700 border-b-2 border-indigo-600' : 'text-slate-600 hover:bg-slate-100'}`}
          >
            {isImageKind ? <ImageIcon className="w-4 h-4 inline mr-1.5" /> : <FileText className="w-4 h-4 inline mr-1.5" />}
            Pick from {isImageKind ? 'Media' : 'Documents'}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {tab === 'upload' ? (
            <label className="flex flex-col items-center justify-center w-full border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 py-12">
              <Upload className="w-10 h-10 text-slate-400 mb-3" />
              <p className="text-sm font-medium text-slate-600">Tap or drag file to upload</p>
              <p className="text-xs text-slate-400 mt-1">
                {isImageKind ? 'JPG, PNG, GIF, WebP up to 10MB' : 'PDF, Word, Excel, PowerPoint, CSV up to 50MB'}
              </p>
              <input
                type="file"
                className="hidden"
                accept={acceptAttr}
                onChange={handleUploadClick}
                data-testid="picker-upload-input"
              />
            </label>
          ) : (
            <>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder={`Search ${isImageKind ? 'images' : 'documents'}...`}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  data-testid="picker-search"
                />
              </div>

              {loading ? (
                <div className="text-center py-12 text-slate-500 text-sm">Loading library…</div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-12 text-slate-500 text-sm">
                  {items.length === 0 ? 'Library is empty. Upload your first file from the Upload tab.' : 'No items match your search.'}
                </div>
              ) : isImageKind ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {filtered.map(it => (
                    <button
                      key={it.id}
                      onClick={() => handlePickLibrary(it)}
                      data-testid={`picker-item-${it.id}`}
                      className="group relative aspect-square bg-slate-100 rounded-lg overflow-hidden border border-slate-200 hover:ring-2 hover:ring-indigo-500"
                    >
                      <img src={`${BACKEND_URL}${it.url}`} alt={it.original_name} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Check className="w-6 h-6 text-white" />
                      </div>
                      <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] px-1.5 py-1 truncate">
                        {it.original_name}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {filtered.map(it => (
                    <button
                      key={it.id}
                      onClick={() => handlePickLibrary(it)}
                      data-testid={`picker-item-${it.id}`}
                      className="w-full text-left flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:border-indigo-400 hover:bg-indigo-50/40"
                    >
                      <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{it.original_name}</p>
                        <p className="text-xs text-slate-500 truncate">
                          {it.category} • {(it.file_size / 1024).toFixed(1)}KB
                          {it.description && ` • ${it.description}`}
                        </p>
                      </div>
                      <Check className="w-4 h-4 text-indigo-500 opacity-0 group-hover:opacity-100" />
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilePickerDialog;
